import { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetTargetareNomenclatoare } from "@/hooks/api/query/useGetTargetareNomenclatoare";
import { formatNumar } from "@/utils/targetareFormatting";
import { HeroPagina } from "@/pages/AdministrareTargetare/components/HeroPagina";

export default function Nomenclatoare() {
  const nomenclatoare = useGetTargetareNomenclatoare();
  const date = nomenclatoare.data;

  const caenTotal = useMemo(
    () => (date?.caen_versiuni ?? []).reduce((acc, r) => acc + r.firme, 0),
    [date]
  );

  return (
    <div className="space-y-6">
      <HeroPagina
        titlu="Nomenclatoare"
        descriere={
          <>
            Referințele folosite la aplicarea snapshot-urilor: categoria
            fiecărei stări ONRC (funcțiune, întrerupere, insolvență, dizolvare,
            radiere…), distribuția codurilor CAEN curente pe versiune (Rev. 2 /
            Rev. 3) și cursul BNR pe ani. Toate sunt doar de citire aici; se
            modifică prin migrație.
          </>
        }
        isLoading={nomenclatoare.isLoading}
        coloane={3}
        tiles={[
          { eticheta: "Coduri de stare", valoare: date?.stari.length },
          {
            eticheta: "Categorii de stare",
            valoare: date?.stari_pe_categorie.length,
          },
          { eticheta: "Firme cu CAEN curent", valoare: caenTotal },
        ]}
      />

      {nomenclatoare.error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Nomenclatoarele nu au putut fi încărcate:{" "}
          {nomenclatoare.error.message}
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-md border">
          <p className="border-b px-3 py-2 text-sm font-medium">
            Stări ONRC pe categorie
          </p>
          {nomenclatoare.isLoading ? (
            <div className="space-y-2 p-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {(date?.stari_pe_categorie ?? []).map((r) => (
                  <tr key={r.categorie} className="border-t">
                    <td className="px-3 py-1.5">{r.categorie}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {formatNumar(r.coduri)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-md border">
          <p className="border-b px-3 py-2 text-sm font-medium">
            Coduri CAEN curente pe versiune / sursă
          </p>
          {nomenclatoare.isLoading ? (
            <div className="space-y-2 p-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5 font-medium">Versiune</th>
                  <th className="px-3 py-1.5 font-medium">Sursă</th>
                  <th className="px-3 py-1.5 font-medium text-right">Firme</th>
                </tr>
              </thead>
              <tbody>
                {(date?.caen_versiuni ?? []).map((r) => (
                  <tr key={`${r.ver}-${r.sursa}`} className="border-t">
                    <td className="px-3 py-1.5">Rev. {r.ver}</td>
                    <td className="px-3 py-1.5">{r.sursa}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {formatNumar(r.firme)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="rounded-md border">
        <p className="border-b px-3 py-2 text-sm font-medium">Curs BNR</p>
        {nomenclatoare.isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-4 w-full" />
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-1.5 font-medium">An</th>
                <th className="px-3 py-1.5 font-medium">Curs RON/EUR</th>
                <th className="px-3 py-1.5 font-medium">Data</th>
                <th className="px-3 py-1.5 font-medium">Sursă</th>
              </tr>
            </thead>
            <tbody>
              {(date?.curs_bnr ?? []).map((r) => (
                <tr key={r.an} className="border-t">
                  <td className="px-3 py-1.5 font-mono">{r.an}</td>
                  <td className="px-3 py-1.5 tabular-nums">{r.curs}</td>
                  <td className="px-3 py-1.5 font-mono">{r.data}</td>
                  <td className="px-3 py-1.5">{r.sursa}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-md border">
        <p className="border-b px-3 py-2 text-sm font-medium">
          Toate codurile de stare ({date?.stari.length ?? 0})
        </p>
        {nomenclatoare.isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-4 w-full" />
          </div>
        ) : (
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/60 text-left text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5 font-medium">Cod</th>
                  <th className="px-3 py-1.5 font-medium">Denumire</th>
                  <th className="px-3 py-1.5 font-medium">Categorie</th>
                </tr>
              </thead>
              <tbody>
                {(date?.stari ?? []).map((s) => (
                  <tr key={s.cod} className="border-t">
                    <td className="whitespace-nowrap px-3 py-1 font-mono">
                      {s.cod}
                    </td>
                    <td className="px-3 py-1">{s.denumire}</td>
                    <td className="px-3 py-1">{s.categorie ?? "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
